import type { TOC } from '@ember/component/template-only';
import { fn } from '@ember/helper';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import { didInsert } from '@ember/render-modifiers';
import {
  PrismButton,
  PrismCard,
  PrismComboSelectInput,
  PrismInput,
  PrismSelectInput,
} from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { Illustration } from 'frontend-utils';
import GeneralButtonsChoiceSegmentButton from 'lumen/components/general/buttons/choice-segment-button';
import GeneralNavigationIntercomHelpLink from 'lumen/components/general/navigation/intercom-help-link';
import SettingsMyotbOtbSelfServiceDataBar from 'lumen/components/settings/myotb/otb-self-service-data-bar';
import type HotelSettingsMyotbPmsSelectionController from 'lumen/controllers/hotel/settings/myotb/pms-selection';

interface HotelSettingsMyotbPmsSelectionSignature {
  Args: {
    controller: HotelSettingsMyotbPmsSelectionController;
    model: unknown;
  };
}

<template>
  <PrismCard class="myotb-card">
    <:header as |Header|>
      <div class="grid gap-400">
        <SettingsMyotbOtbSelfServiceDataBar />
        <Header.title>
          {{t "settings.my_otb.pms_selection_form.pms_label"}}
        </Header.title>
      </div>
    </:header>

    <:content>
      <div
        class="myotb-card__content-container flex flex-col gap-400"
        {{didInsert @controller.setDefaults}}
      >
        {{#if @controller.topPmss.length}}
          <div class="grid grid-cols-3 gap-400">
            {{#each @controller.topPmss as |topPms|}}
              <GeneralButtonsChoiceSegmentButton
                @isChecked={{topPms.isSelected}}
                @label={{topPms.display_name}}
                @onClick={{fn @controller.onSelectPms topPms}}
                @type="radio"
              >
                {{#if topPms.imageHtml}}
                  <Illustration
                    @html={{topPms.imageHtml}}
                    aria-label={{topPms.display_name}}
                    class="myotb-card__pms-image"
                  />
                {{/if}}
              </GeneralButtonsChoiceSegmentButton>

            {{/each}}
          </div>
        {{/if}}

        {{#if @controller.hasSelectedDifferentPms}}
          <div class="flex flex-col gap-100">
            <label class="text-200--bold">
              {{t "settings.my_otb.pms_selection_form.title"}}
            </label>
            <div class="myotb-selection-input-container flex flex-col">
              <PrismComboSelectInput
                @isSearchable={{true}}
                @isSecondary={{true}}
                @onChange={{@controller.onSelectPms}}
                @options={{@controller.pmsSelectionOptions}}
                @searchMatcher={{@controller.pmsAndOtherSystemSearchMatcher}}
                @value={{@controller.selectedPmsOption}}
              />
            </div>
          </div>
        {{/if}}

        {{#if @controller.hasMultiplePmsVersions}}
          <div class="flex flex-col gap-100">
            <label class="text-200--bold">
              {{t
                "settings.my_otb.pms_selection.select_your_version"
                pmsName=@controller.selectedPms.display_name
              }}
            </label>
            <div class="myotb-selection-input-container flex flex-col">
              <PrismSelectInput
                @isSecondary={{true}}
                @onChange={{@controller.onSelectPmsVersion}}
                @options={{@controller.selectedPmsVersionOptions}}
                @value={{@controller.selectedPmsVersionValue}}
              />
            </div>
          </div>
          {{#if @controller.selectedPms.version_determination_help_article}}
            <GeneralNavigationIntercomHelpLink
              @articleLink={{@controller.selectedPms.version_determination_help_article}}
              @iconName="link-out"
              @isStandaloneLink={{true}}
              @linkText={{t
                "settings.my_otb.pms_selection_form.help"
                pmsName=@controller.selectedPms.display_name
              }}
              @type="text-and-icon"
            />
          {{/if}}
        {{else if @controller.hasSelectedOtherPms}}
          <div class="flex flex-col gap-100">
            <label class="text-200--bold">
              {{t "settings.my_otb.pms_selection.enter_pms_name"}}
            </label>
            <div class="myotb-selection-input-container flex flex-col">
              <PrismInput
                @onInput={{@controller.updateOtherPmsName}}
                @removePadding={{true}}
                @value={{@controller.otherPmsName}}
              />
            </div>
          </div>
        {{/if}}
      </div>
    </:content>

    <:footer>
      <div class="flex justify-between gap-300">
        <PrismButton
          @buttonType="ghost"
          @iconBefore="arrow-left"
          @label={{t "general.back"}}
          @onClick={{@controller.onClickPrevious}}
        />
        <PrismButton
          @iconAfter="arrow-right"
          @isDisabled={{@controller.isContinueDisabled}}
          @isLoading={{@controller.isLoading}}
          @label={{t "general.continue"}}
          @onClick={{@controller.onClickContinue}}
        />
      </div>
    </:footer>
  </PrismCard>
</template> satisfies TOC<HotelSettingsMyotbPmsSelectionSignature>;
